import React, { useEffect, useState, useRef } from 'react';
import { Spinner } from "@tag/tag-components-react-v4";
import PipelineStatusService from "../services/PipelineStatusService";

interface PipelineStatusDisplayProps {
    apiBaseUrl: string | undefined;
    pipelineId: number;
    runId: number;
    buildUrl?: string;
    onComplete?: (result: string | null) => void;
}

interface StageStatus {
    name: string;
    state: string;
    result: string | null;
}

interface PipelineStatus {
    state: string;
    result: string | null;
    buildUrl: string | null;
    stages: StageStatus[] | null;
}

const POLL_INTERVAL_MS = 7000;

const inlineSpinnerStyle: React.CSSProperties = {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid #e0e0e0',
    borderTop: '2px solid #1976d2',
    borderRadius: '50%',
    animation: 'pipeline-spin 0.8s linear infinite',
    flexShrink: 0,
};

const spinKeyframes = `@keyframes pipeline-spin { to { transform: rotate(360deg); } }`;

function PipelineStatusDisplay({ apiBaseUrl, pipelineId, runId, buildUrl, onComplete }: PipelineStatusDisplayProps) {
    const [status, setStatus] = useState<PipelineStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isTerminalState = (state: string, result: string | null): boolean => {
        if (state === 'completed') return true;
        if (state === 'canceling' && result === 'canceled') return true;
        return false;
    };

    useEffect(() => {
        let mounted = true;

        const fetchStatus = async () => {
            try {
                const result = await PipelineStatusService.getStatus(apiBaseUrl, pipelineId, runId);
                if (!mounted) return;

                setStatus({
                    state: result.state,
                    result: result.result,
                    buildUrl: result.buildUrl,
                    stages: result.stages
                });
                setError(null);

                if (isTerminalState(result.state, result.result)) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    onComplete?.(result.result);
                }
            } catch {
                if (!mounted) return;
                setError("Unable to fetch pipeline status");
            }
        };

        fetchStatus();
        intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

        return () => {
            mounted = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [apiBaseUrl, pipelineId, runId]);

    const resolvedBuildUrl = status?.buildUrl || buildUrl;

    const getStatusLabel = (): string => {
        if (!status) return 'Checking status...';

        if (status.state === 'completed') {
            switch (status.result) {
                case 'succeeded': return 'Succeeded';
                case 'failed': return 'Failed';
                case 'canceled': return 'Cancelled';
                default: return status.result || 'Unknown';
            }
        }

        if (status.state === 'canceling') {
            return status.result === 'canceled' ? 'Cancelled' : 'Cancelling...';
        }

        if (status.state === 'inProgress') return 'In Progress';

        return status.state;
    };

    const getStatusColor = (): string => {
        if (!status || status.state === 'inProgress') return '#1976d2';
        if (status.state === 'completed') {
            if (status.result === 'succeeded') return '#2e7d32';
            if (status.result === 'failed') return '#d32f2f';
            if (status.result === 'canceled') return '#ed6c02';
            return '#ed6c02';
        }
        if (status.state === 'canceling') return '#ed6c02';
        return '#ed6c02';
    };

    const isFailed = status?.state === 'completed' && status?.result === 'failed';
    const isCancelled = status?.result === 'canceled';
    const isInProgress = !status || (status.state === 'inProgress') ||
        (status.state === 'canceling' && status.result !== 'canceled');

    const getStageColor = (stage: StageStatus): string => {
        if (stage.result === 'succeeded') return '#2e7d32';
        if (stage.result === 'failed') return '#d32f2f';
        if (stage.result === 'canceled' || stage.result === 'skipped') return '#9e9e9e';
        if (stage.state === 'inProgress') return '#1976d2';
        return '#757575';
    };

    const getStageIcon = (stage: StageStatus): React.ReactNode => {
        if (stage.state === 'inProgress') {
            return <span style={inlineSpinnerStyle} />;
        }
        if (stage.result === 'succeeded') {
            return <span style={{ color: '#2e7d32', fontSize: '14px', lineHeight: '14px' }}>{'\u2714'}</span>;
        }
        if (stage.result === 'failed') {
            return <span style={{ color: '#d32f2f', fontSize: '14px', lineHeight: '14px' }}>{'\u2718'}</span>;
        }
        if (stage.result === 'canceled' || stage.result === 'skipped') {
            return <span style={{ color: '#9e9e9e', fontSize: '14px', lineHeight: '14px' }}>{'\u2013'}</span>;
        }
        return <span style={{ color: '#bdbdbd', fontSize: '14px', lineHeight: '14px' }}>{'\u25CB'}</span>;
    };

    const getProgressSummary = (): string | null => {
        if (!status?.stages || status.stages.length === 0) return null;

        const completed = status.stages.filter(s => s.state === 'completed').length;
        const total = status.stages.length;

        return `${completed} of ${total} completed`;
    };

    return (
        <div style={{ marginTop: '12px' }}>
            <style>{spinKeyframes}</style>

            {/* Overall status header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: isInProgress ? '#e3f2fd' : isFailed ? '#fce4ec' : isCancelled ? '#fff3e0' : '#e8f5e9',
                borderRadius: '6px',
                borderLeft: `4px solid ${getStatusColor()}`,
                marginBottom: '12px',
            }}>
                {isInProgress && <Spinner size="small" />}
                <span style={{ fontWeight: 600, color: getStatusColor(), fontSize: '14px' }}>
                    Pipeline Status: {getStatusLabel()}
                </span>
            </div>

            {error && (
                <div style={{ color: '#ed6c02', fontSize: '13px', marginBottom: '8px' }}>{error}</div>
            )}
            {isFailed && (
                <div style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '8px' }}>
                    The pipeline run has failed. Please check the pipeline run for details.
                </div>
            )}
            {isCancelled && (
                <div style={{ color: '#ed6c02', fontSize: '13px', marginBottom: '8px' }}>
                    The pipeline run was cancelled.
                </div>
            )}

            {/* Stages progress */}
            {status?.stages && status.stages.length > 0 && (
                <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '12px',
                }}>
                    <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #e0e0e0',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#424242',
                    }}>
                        Progress: {getProgressSummary()}
                    </div>
                    <div style={{ padding: '4px 0' }}>
                        {status.stages.map((stage, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 12px',
                                fontSize: '13px',
                                backgroundColor: stage.state === 'inProgress' ? '#f3f8ff' : 'transparent',
                                borderBottom: index < status.stages!.length - 1 ? '1px solid #f5f5f5' : 'none',
                            }}>
                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', flexShrink: 0 }}>
                                    {getStageIcon(stage)}
                                </span>
                                <span style={{ color: getStageColor(stage), flex: 1 }}>{stage.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {resolvedBuildUrl && (
                <a href={resolvedBuildUrl} target="_blank" rel="noopener noreferrer"
                   style={{ fontSize: '13px' }}>
                    View Pipeline Run
                </a>
            )}
        </div>
    );
}

export default PipelineStatusDisplay;
